import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "antd";
import { Toaster, toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Pagination from "components/pagination";
import { apiCall } from "utils/apiClient";

const LEAD_STATUS_BADGES = {
  new: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  converted: "bg-emerald-100 text-emerald-700",
  closed: "bg-gray-100 text-gray-700",
};

const WITHDRAWAL_STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "Paid", value: "paid" },
];

const WITHDRAWAL_STATUS_BADGES = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  paid: "bg-blue-100 text-blue-700",
};

const TRANSACTION_SOURCE_OPTIONS = [
  { label: "All Source Types", value: "" },
  { label: "Earning", value: "earning" },
  { label: "Withdrawal", value: "withdrawal" },
  { label: "Manual", value: "manual" },
  { label: "Refund", value: "refund" },
];

const TRANSACTION_DIRECTION_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "Credit", value: "1" },
  { label: "Debit", value: "0" },
];

const TAB_OPTIONS = [
  { key: "summary", label: "Summary" },
  { key: "details", label: "Details" },
  { key: "leads", label: "Leads" },
  { key: "wallet", label: "Wallet Transaction" },
  { key: "withdrawals", label: "Request Wallet" },
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value || "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const getPartnerStatusBadgeClass = (status) =>
  status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700";

const getVerificationBadgeClass = (value) =>
  Number(value) === 1 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700";

const InfoCard = ({ label, value, className = "" }) => (
  <div className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-2 break-words text-sm font-semibold text-gray-900">{value || "-"}</div>
  </div>
);

InfoCard.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  className: PropTypes.string,
};

const MetricCard = ({ label, value, tone = "default" }) => {
  const tones = {
    default: "border-gray-200 bg-white text-gray-900",
    blue: "border-blue-200 bg-blue-50 text-blue-900",
    green: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    red: "border-red-200 bg-red-50 text-red-900",
  };

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${tones[tone] || tones.default}`}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </div>
  );
};

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.string,
};

const ChannelPartnerTabs = ({ activeTab, setActiveTab }) => (
  <div className="flex flex-wrap gap-6 border-b border-gray-200 px-6 pt-4">
    {TAB_OPTIONS.map((tab) => (
      <button
        key={tab.key}
        type="button"
        onClick={() => setActiveTab(tab.key)}
        className={`pb-3 text-sm font-medium ${activeTab === tab.key
          ? "border-b-2 border-blue-600 text-blue-600"
          : "text-gray-500 hover:text-gray-700"
          }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

ChannelPartnerTabs.propTypes = {
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
};

const ChannelPartnerDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const partner = location.state?.partner;
  const [activeTab, setActiveTab] = useState("summary");
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");

  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState("");

  const [withdrawRequests, setWithdrawRequests] = useState([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
  const [withdrawalsError, setWithdrawalsError] = useState("");
  const [withdrawalStatus, setWithdrawalStatus] = useState("");
  const [withdrawalPage, setWithdrawalPage] = useState(1);
  const [withdrawalLimit, setWithdrawalLimit] = useState(20);
  const [withdrawalTotalPages, setWithdrawalTotalPages] = useState(0);
  const [withdrawalTotalRecords, setWithdrawalTotalRecords] = useState(0);

  const [walletTransactions, setWalletTransactions] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState("");
  const [walletPage, setWalletPage] = useState(1);
  const [walletLimit, setWalletLimit] = useState(20);
  const [walletTotalPages, setWalletTotalPages] = useState(0);
  const [walletTotalRecords, setWalletTotalRecords] = useState(0);
  const [walletSourceType, setWalletSourceType] = useState("");
  const [walletIsCredit, setWalletIsCredit] = useState("");

  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [isCredit, setIsCredit] = useState("1");
  const [sourceType, setSourceType] = useState("manual");
  const [sourceId, setSourceId] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [nextStatus, setNextStatus] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [rejectedReason, setRejectedReason] = useState("");
  const [actionSubmitting, setActionSubmitting] = useState(false);

  const [bankDetails, setBankDetails] = useState(null);
  const [isLoadingBank, setIsLoadingBank] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState(false);
  const [bankError, setBankError] = useState("");

  if (!partner) {
    navigate("/channel-partner");
    return null;
  }

  const resolvedPartner = summaryData?.channel_partner || summaryData?.partner || partner;

  const fetchSummary = async () => {
    setSummaryLoading(true);
    setSummaryError("");

    const result = await apiCall({
      endpoint: `admin/channel-partner/summary/${partner.id}`,
      method: "GET",
    });

    if (result?.status === 200) {
      setSummaryData(result.data || null);
      setSummaryLoading(false);
      return;
    }

    setSummaryData(null);
    setSummaryError(result?.message || "Unable to load channel partner summary.");
    setSummaryLoading(false);
  };

  const fetchLeads = async () => {
    setLeadsLoading(true);
    setLeadsError("");
    if (summaryData?.recent_leads) {
      setLeads(Array.isArray(summaryData.recent_leads) ? summaryData.recent_leads : []);
      setLeadsLoading(false);
      return;
    }

    const result = await apiCall({
      endpoint: `admin/channel-partner/leads/${partner.id}`,
      method: "GET",
      params: { page: "1", limit: "100" },
    });

    if (result?.status === 200) {
      setLeads(Array.isArray(result.data) ? result.data : []);
      setLeadsLoading(false);
      return;
    }

    setLeads([]);
    setLeadsError(result?.message || "Unable to load channel partner leads.");
    setLeadsLoading(false);
  };

  const fetchWithdrawalRequests = async () => {
    setWithdrawalsLoading(true);
    setWithdrawalsError("");

    const result = await apiCall({
      endpoint: "admin/channel-partner/wallet/withdraw-requests",
      method: "GET",
      params: {
        page: String(withdrawalPage),
        limit: String(withdrawalLimit),
        ...(withdrawalStatus ? { status: withdrawalStatus } : {}),
        channel_partner_id: String(partner.id),
      },
    });

    if (result?.status === 200) {
      setWithdrawRequests(Array.isArray(result.data) ? result.data : []);
      setWithdrawalTotalRecords(result.pagination?.total_records || 0);
      setWithdrawalTotalPages(result.pagination?.total_pages || 0);
      setWithdrawalsLoading(false);
      return;
    }

    setWithdrawRequests([]);
    setWithdrawalTotalRecords(0);
    setWithdrawalTotalPages(0);
    setWithdrawalsError(result?.message || "Unable to load withdrawal requests.");
    setWithdrawalsLoading(false);
  };

  const fetchWalletTransactions = async () => {
    setWalletLoading(true);
    setWalletError("");

    const result = await apiCall({
      endpoint: `admin/channel-partner/wallet/transactions/${partner.id}`,
      method: "GET",
      params: {
        page: String(walletPage),
        limit: String(walletLimit),
        ...(walletSourceType ? { source_type: walletSourceType } : {}),
        ...(walletIsCredit !== "" ? { is_credit: walletIsCredit } : {}),
      },
    });

    if (result?.status === 200) {
      const payload = result.data || {};
      setWalletTransactions(Array.isArray(payload.transactions) ? payload.transactions : []);
      setWalletTotalRecords(payload.pagination?.total_records || result.pagination?.total_records || 0);
      setWalletTotalPages(payload.pagination?.total_pages || result.pagination?.total_pages || 0);
      setWalletLoading(false);
      return;
    }

    setWalletTransactions([]);
    setWalletTotalRecords(0);
    setWalletTotalPages(0);
    setWalletError(result?.message || "Unable to load wallet transactions.");
    setWalletLoading(false);
  };

  useEffect(() => {
    fetchSummary();
  }, [partner.id]);

  useEffect(() => {
    fetchLeads();
  }, [partner.id, summaryData]);

  useEffect(() => {
    fetchWithdrawalRequests();
  }, [partner.id, withdrawalPage, withdrawalLimit, withdrawalStatus]);

  useEffect(() => {
    fetchWalletTransactions();
  }, [partner.id, walletPage, walletLimit, walletSourceType, walletIsCredit]);

  useEffect(() => {
    if (activeTab === "details") {
      fetchBankDetails();
    }
  }, [activeTab, resolvedPartner?.id]);


  useEffect(() => {
    setBankDetails(null);
    setHasBankDetails(false);
    setBankError("");
  }, [resolvedPartner?.id]);

  const fetchBankDetails = async () => {
    if (!resolvedPartner?.id) return;

    setIsLoadingBank(true);
    setBankError("");

    try {
      const response = await apiCall({
        endpoint: `/channel-partner/bank-details/${resolvedPartner.id}`,
        method: "GET",
      });

      const statusCode = Number(
        response?.status ?? response?.statusCode ?? response?.code ?? 0
      );

      const isSuccess =
        statusCode === 200 ||
        statusCode === 201 ||
        response?.success === true;

      if (!isSuccess) {
        setHasBankDetails(false);
        setBankDetails(null);

        if (statusCode !== 404) {
          setBankError(response?.message || "Unable to load bank details");
        }

        return;
      }

      const data = response?.data || response;

      setBankDetails(data || null);
      setHasBankDetails(true);
    } catch (error) {
      console.error("Bank Details Error:", error);
      setBankDetails(null);
      setHasBankDetails(false);
      setBankError("Something went wrong while fetching bank details");
    } finally {
      setIsLoadingBank(false);
    }
  };

  const closeTransactionModal = () => {
    setTransactionModalOpen(false);
    setAmount("");
    setIsCredit("1");
    setSourceType("manual");
    setSourceId("");
    setNote("");
    setSubmitting(false);
  };

  const createManualWalletTransaction = async () => {
    const normalizedAmount = String(amount).trim();
    if (!normalizedAmount || Number(normalizedAmount) <= 0) {
      toast.error("Please enter a valid amount.");
      return;
    }

    if (sourceType !== "manual" && !String(sourceId).trim()) {
      toast.error("Source ID is required for this source type.");
      return;
    }

    setSubmitting(true);

    const payload = new URLSearchParams({
      channel_partner_id: String(partner.id),
      amount: normalizedAmount,
      is_credit: isCredit,
      source_type: sourceType,
      note,
    });

    if (sourceType !== "manual") {
      payload.append("source_id", String(sourceId).trim());
    }

    const result = await apiCall({
      endpoint: "admin/channel-partner/wallet/manual-transaction",
      method: "POST",
      data: payload,
    });

    if (result?.status === 201) {
      toast.success(result.message || "Manual wallet transaction created successfully.");
      closeTransactionModal();
      fetchSummary();
      fetchWalletTransactions();
      fetchWithdrawalRequests();
      return;
    }

    setSubmitting(false);
    toast.error(result?.message || "Unable to create manual wallet transaction.");
  };

  const openActionModal = (request, statusValue) => {
    setSelectedRequest(request);
    setNextStatus(statusValue);
    setActionNote("");
    setRejectedReason("");
    setActionModalOpen(true);
  };

  const closeActionModal = () => {
    setActionModalOpen(false);
    setSelectedRequest(null);
    setNextStatus("");
    setActionNote("");
    setRejectedReason("");
    setActionSubmitting(false);
  };

  const getActionLabel = () => {
    if (nextStatus === "approved") return "Approve";
    if (nextStatus === "rejected") return "Reject";
    if (nextStatus === "paid") return "Mark as Paid";
    return "Update";
  };

  const updateWithdrawalRequestStatus = async () => {
    if (!selectedRequest?.id || !nextStatus) return;

    if (nextStatus === "rejected" && !rejectedReason.trim()) {
      toast.error("Rejected reason is required.");
      return;
    }

    setActionSubmitting(true);

    const payload = {
      status: nextStatus,
      approved_by_admin_id: localStorage.getItem("id") || "0",
      note: actionNote,
      rejected_reason: nextStatus === "rejected" ? rejectedReason : "",
    };

    const result = await apiCall({
      endpoint: `admin/channel-partner/wallet/withdraw-requests/${selectedRequest.id}/status`,
      method: "PUT",
      data: payload,
    });

    if (result?.status === 200) {
      toast.success(result.message || "Withdrawal request updated successfully.");
      closeActionModal();
      fetchWithdrawalRequests();
      return;
    }

    setActionSubmitting(false);
    toast.error(result?.message || "Unable to update withdrawal request.");
  };

  const leadSummary = useMemo(
    () => ({
      total:
        summaryData?.lead_summary?.total ??
        leads.reduce((acc, lead) => acc + 1, 0),
      new:
        summaryData?.lead_summary?.new ??
        leads.filter((lead) => lead.status === "new").length,
      in_progress:
        summaryData?.lead_summary?.in_progress ??
        leads.filter((lead) => lead.status === "in_progress").length,
      converted:
        summaryData?.lead_summary?.converted ??
        leads.filter((lead) => lead.status === "converted").length,
      closed:
        summaryData?.lead_summary?.closed ??
        leads.filter((lead) => lead.status === "closed").length,
    }),
    [leads, summaryData]
  );

  const withdrawalSummary = useMemo(
    () =>
      withdrawRequests.reduce(
        (acc, request) => {
          const amountValue = Number(request.amount) || 0;
          acc.total += 1;
          acc.totalAmount += amountValue;
          acc[request.status] = (acc[request.status] || 0) + 1;
          if (request.status === "approved") acc.approvedAmount += amountValue;
          if (request.status === "paid") acc.paidAmount += amountValue;
          if (request.status === "pending") acc.pendingAmount += amountValue;
          return acc;
        },
        {
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0,
          paid: 0,
          totalAmount: 0,
          approvedAmount: 0,
          paidAmount: 0,
          pendingAmount: 0,
        }
      ),
    [withdrawRequests]
  );



  const walletSummary = summaryData?.wallet_summary || {};
  const monthlySummary = summaryData?.monthly_summary || {};

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      <div className="mt-6 px-4 sm:px-6">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <button
                  onClick={() => navigate("/channel-partner")}
                  className="mb-3 text-sm font-medium text-blue-600 hover:underline"
                >
                  Back to Channel Partner List
                </button>
                <h1 className="text-2xl font-semibold text-gray-900">{resolvedPartner.name || "Channel Partner"}</h1>
                <p className="mt-1 text-sm text-gray-500">
                  {resolvedPartner.company_name || "No company name available"}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getPartnerStatusBadgeClass(
                    resolvedPartner.status
                  )}`}
                >
                  {resolvedPartner.status || "-"}
                </span>
                <button
                  type="button"
                  onClick={() => setActiveTab("leads")}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Leads
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("withdrawals")}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                >
                  Request Wallet
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("wallet")}
                  className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600"
                >
                  Wallet Transaction
                </button>
              </div>
            </div>
          </div>

          <ChannelPartnerTabs activeTab={activeTab} setActiveTab={setActiveTab} />

          <div className="p-6">
            {activeTab === "summary" && (
              <div className="space-y-6">
                {summaryLoading && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    Loading channel partner summary...
                  </div>
                )}

                {summaryError && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    {summaryError}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <MetricCard label="Total Leads" value={leadSummary.total} tone="blue" />
                  <MetricCard label="Converted Leads" value={leadSummary.converted} tone="green" />
                  <MetricCard
                    label="Wallet Balance"
                    value={formatAmount(walletSummary.wallet_balance ?? walletSummary.balance)}
                    tone="green"
                  />
                  <MetricCard label="Pending Withdrawals" value={withdrawalSummary.pending} tone="amber" />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <MetricCard label="Pending Amount" value={formatAmount(withdrawalSummary.pendingAmount)} tone="amber" />
                  <MetricCard
                    label="Total Credits"
                    value={formatAmount(walletSummary.total_credit ?? walletSummary.credit_total)}
                    tone="blue"
                  />
                  <MetricCard
                    label="Total Debits"
                    value={formatAmount(walletSummary.total_debit ?? walletSummary.debit_total)}
                    tone="red"
                  />
                  <MetricCard
                    label="This Month Earnings"
                    value={formatAmount(monthlySummary.total_earnings ?? monthlySummary.earnings)}
                    tone="default"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <InfoCard label="Channel Partner ID" value={resolvedPartner.id} />
                  <InfoCard label="Email" value={resolvedPartner.email} />
                  <InfoCard label="Mobile" value={resolvedPartner.mobile} />
                  <InfoCard label="Last Login" value={formatDateTime(resolvedPartner.last_login_at)} />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <MetricCard label="Recent Leads" value={leads.length} tone="blue" />
                  <MetricCard label="Monthly Leads" value={monthlySummary.total_leads ?? "-"} tone="amber" />
                  <MetricCard label="Approved Withdrawals" value={withdrawalSummary.approved} tone="green" />
                  <MetricCard label="Paid Withdrawals" value={withdrawalSummary.paid} tone="green" />
                </div>
              </div>
            )}

            {activeTab === "details" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <InfoCard label="Channel Partner ID" value={resolvedPartner.id} />
                  <InfoCard label="Name" value={resolvedPartner.name} />
                  <InfoCard label="Company Name" value={resolvedPartner.company_name} />
                  <InfoCard label="Email" value={resolvedPartner.email} />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <InfoCard label="Mobile" value={resolvedPartner.mobile} />
                  <InfoCard label="Created At" value={formatDateTime(resolvedPartner.created_at)} />
                  <InfoCard label="Updated At" value={formatDateTime(resolvedPartner.updated_at)} />
                  <InfoCard
                    label="Login Status"
                    value={Number(resolvedPartner.is_logged_in) === 1 ? "Logged In" : "Logged Out"}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">

                  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Email Verification</div>
                    <div className="mt-2">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getVerificationBadgeClass(
                          resolvedPartner.email_verified
                        )}`}
                      >
                        {Number(resolvedPartner.email_verified) === 1 ? "Verified" : "Not Verified"}
                      </span>
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Mobile Verification</div>
                    <div className="mt-2">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getVerificationBadgeClass(
                          resolvedPartner.mobile_verified
                        )}`}
                      >
                        {Number(resolvedPartner.mobile_verified) === 1 ? "Verified" : "Not Verified"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Bank Details
                  </div>

                  {isLoadingBank ? (
                    <div className="text-sm text-gray-500">Loading...</div>
                  ) : !hasBankDetails ? (
                    <div className="text-sm text-gray-400">No bank details available</div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                      <InfoCard label="Account Holder" value={bankDetails?.account_holder_name} />
                      <InfoCard label="Bank Name" value={bankDetails?.bank_name} />
                      <InfoCard label="Branch" value={bankDetails?.bank_branch} />
                      <InfoCard label="Account Number" value={bankDetails?.account_number} />
                      <InfoCard label="IFSC" value={bankDetails?.ifsc_code} />
                      <InfoCard label="UPI ID" value={bankDetails?.upi_id} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "leads" && (
              <div className="space-y-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900">Channel Partner Leads</div>
                  <div className="text-xs text-gray-500">Showing latest leads linked to this channel partner</div>
                </div>

                {leadsError && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    {leadsError}
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm text-left text-gray-700">
                    <thead className="text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="px-3 py-2 whitespace-nowrap">Sr No</th>
                        <th className="px-3 py-2 whitespace-nowrap">Customer</th>
                        <th className="px-3 py-2 whitespace-nowrap">Mobile</th>
                        <th className="px-3 py-2 whitespace-nowrap">Requirement</th>
                        <th className="px-3 py-2 whitespace-nowrap">Space Type</th>
                        <th className="px-3 py-2 whitespace-nowrap text-right">Budget</th>
                        <th className="px-3 py-2 whitespace-nowrap">Status</th>
                        <th className="px-3 py-2 whitespace-nowrap">Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leadsLoading ? (
                        <tr>
                          <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                            Loading leads...
                          </td>
                        </tr>
                      ) : leads.length > 0 ? (
                        leads.map((lead, index) => (
                          <tr key={lead.id || `${partner.id}-${index}`} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">{index + 1}</td>
                            <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">
                              {lead.customer_name || "-"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{lead.mobile || "-"}</td>
                            <td className="px-3 py-2 min-w-[220px]">{lead.requirement_title || "-"}</td>
                            <td className="px-3 py-2 whitespace-nowrap uppercase">{lead.space_type || "-"}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-right">{formatAmount(lead.budget)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${LEAD_STATUS_BADGES[lead.status] || "bg-gray-100 text-gray-700"
                                  }`}
                              >
                                {lead.status ? lead.status.replace(/_/g, " ") : "-"}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(lead.created_at)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                            No leads found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "wallet" && (
              <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">Wallet Transaction</div>
                    <div className="text-xs text-gray-500">
                      Create manual credit or debit entries for this channel partner wallet
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setTransactionModalOpen(true)}
                    className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600"
                  >
                    Create Transaction
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <MetricCard label="Wallet Balance" value={formatAmount(walletSummary.wallet_balance ?? walletSummary.balance)} tone="green" />
                  <MetricCard label="Total Credits" value={formatAmount(walletSummary.total_credit ?? walletSummary.credit_total)} tone="blue" />
                  <MetricCard label="Total Debits" value={formatAmount(walletSummary.total_debit ?? walletSummary.debit_total)} tone="red" />
                  <MetricCard label="Transactions" value={walletTotalRecords} tone="default" />
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <select
                    value={walletSourceType}
                    onChange={(event) => {
                      setWalletSourceType(event.target.value);
                      setWalletPage(1);
                    }}
                    className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                  >
                    {TRANSACTION_SOURCE_OPTIONS.map((option) => (
                      <option key={option.value || "all-source"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={walletIsCredit}
                    onChange={(event) => {
                      setWalletIsCredit(event.target.value);
                      setWalletPage(1);
                    }}
                    className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                  >
                    {TRANSACTION_DIRECTION_OPTIONS.map((option) => (
                      <option key={option.value || "all-direction"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => {
                      setWalletSourceType("");
                      setWalletIsCredit("");
                      setWalletPage(1);
                    }}
                    className="h-9 rounded-md bg-gray-200 px-3 text-sm font-medium text-gray-700 hover:bg-gray-300"
                  >
                    Clear Filters
                  </button>
                </div>

                {walletError && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    {walletError}
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm text-left text-gray-700">
                    <thead className="text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="px-3 py-2 whitespace-nowrap">Sr No</th>
                        <th className="px-3 py-2 whitespace-nowrap">Date</th>
                        <th className="px-3 py-2 whitespace-nowrap">Source Type</th>
                        <th className="px-3 py-2 whitespace-nowrap">Source ID</th>
                        <th className="px-3 py-2 whitespace-nowrap">Type</th>
                        <th className="px-3 py-2 whitespace-nowrap text-right">Amount</th>
                        <th className="px-3 py-2 whitespace-nowrap">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walletLoading ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                            Loading wallet transactions...
                          </td>
                        </tr>
                      ) : walletTransactions.length > 0 ? (
                        walletTransactions.map((transaction, index) => (
                          <tr
                            key={transaction.id || `${partner.id}-transaction-${index}`}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="px-3 py-2 whitespace-nowrap">
                              {(walletPage - 1) * walletLimit + index + 1}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(transaction.created_at)}</td>
                            <td className="px-3 py-2 whitespace-nowrap uppercase">{transaction.source_type || "-"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{transaction.source_id || "-"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${Number(transaction.is_credit) === 1
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                                  }`}
                              >
                                {Number(transaction.is_credit) === 1 ? "Credit" : "Debit"}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right">{formatAmount(transaction.amount)}</td>
                            <td className="px-3 py-2 min-w-[240px]">{transaction.note || "-"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                            No wallet transactions found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={walletPage}
                  totalPages={walletTotalPages}
                  onPageChange={(page) => setWalletPage(page)}
                  recordsPerPage={walletLimit}
                  onRecordsPerPageChange={(value) => {
                    setWalletPage(1);
                    setWalletLimit(value);
                  }}
                  totalRecords={walletTotalRecords}
                  count={walletTransactions.length}
                  showFirstLastControls
                  compact
                />
              </div>
            )}

            {activeTab === "withdrawals" && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">Request Wallet</div>
                    <div className="text-xs text-gray-500">Withdrawal requests for this channel partner</div>
                  </div>

                  <select
                    value={withdrawalStatus}
                    onChange={(event) => {
                      setWithdrawalStatus(event.target.value);
                      setWithdrawalPage(1);
                    }}
                    className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                  >
                    {WITHDRAWAL_STATUS_OPTIONS.map((option) => (
                      <option key={option.value || "all"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {withdrawalsError && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    {withdrawalsError}
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm text-left text-gray-700">
                    <thead className="text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="px-3 py-2 whitespace-nowrap">Sr No</th>
                        <th className="px-3 py-2 whitespace-nowrap">Request ID</th>
                        <th className="px-3 py-2 whitespace-nowrap text-right">Amount</th>
                        <th className="px-3 py-2 whitespace-nowrap">Status</th>
                        <th className="px-3 py-2 whitespace-nowrap">Method</th>
                        <th className="px-3 py-2 whitespace-nowrap">Reference</th>
                        <th className="px-3 py-2 whitespace-nowrap">Created At</th>
                        <th className="px-3 py-2 whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {withdrawalsLoading ? (
                        <tr>
                          <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                            Loading withdrawal requests...
                          </td>
                        </tr>
                      ) : withdrawRequests.length > 0 ? (
                        withdrawRequests.map((request, index) => (
                          <tr key={request.id || `${partner.id}-withdraw-${index}`} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {(withdrawalPage - 1) * withdrawalLimit + index + 1}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">{request.id || "-"}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-right">{formatAmount(request.requested_amount)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${WITHDRAWAL_STATUS_BADGES[request.status] || "bg-gray-100 text-gray-700"
                                  }`}
                              >
                                {request.status || "-"}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{request.method || request.payment_method || "-"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {request.reference || request.reference_no || request.transaction_id || "-"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(request.created_at)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex flex-wrap gap-2">
                                {request.status === "pending" && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => openActionModal(request, "approved")}
                                      className="rounded-md bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openActionModal(request, "rejected")}
                                      className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                                    >
                                      Reject
                                    </button>
                                  </>
                                )}
                                {request.status === "approved" && (
                                  <button
                                    type="button"
                                    onClick={() => openActionModal(request, "paid")}
                                    className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                                  >
                                    Mark Paid
                                  </button>
                                )}
                                {!["pending", "approved"].includes(request.status) && (
                                  <span className="text-xs font-medium text-gray-500">No actions</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                            No withdrawal requests found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={withdrawalPage}
                  totalPages={withdrawalTotalPages}
                  onPageChange={(page) => setWithdrawalPage(page)}
                  recordsPerPage={withdrawalLimit}
                  onRecordsPerPageChange={(value) => {
                    setWithdrawalPage(1);
                    setWithdrawalLimit(value);
                  }}
                  totalRecords={withdrawalTotalRecords}
                  count={withdrawRequests.length}
                  showFirstLastControls
                  compact
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        title="Create Manual Wallet Transaction"
        open={transactionModalOpen}
        onCancel={closeTransactionModal}
        onOk={createManualWalletTransaction}
        okText={submitting ? "Saving..." : "Create Transaction"}
        okButtonProps={{ disabled: submitting }}
        cancelButtonProps={{ disabled: submitting }}
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Channel Partner ID: <span className="font-medium text-gray-900">{resolvedPartner.id || "-"}</span>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Transaction Type</label>
            <select
              value={isCredit}
              onChange={(event) => setIsCredit(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="1">Credit</option>
              <option value="0">Debit</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Source Type</label>
            <select
              value={sourceType}
              onChange={(event) => {
                setSourceType(event.target.value);
                if (event.target.value === "manual") {
                  setSourceId("");
                }
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="manual">Manual</option>
              <option value="earning">Earning</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="refund">Refund</option>
            </select>
          </div>

          {sourceType !== "manual" && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Source ID</label>
              <input
                type="text"
                value={sourceId}
                onChange={(event) => setSourceId(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Enter reference ID"
              />
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Enter amount"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Note</label>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Optional note"
            />
          </div>
        </div>
      </Modal>

      <Modal
        title={`${getActionLabel()} Withdrawal Request`}
        open={actionModalOpen}
        onCancel={closeActionModal}
        onOk={updateWithdrawalRequestStatus}
        okText={actionSubmitting ? "Saving..." : getActionLabel()}
        okButtonProps={{ disabled: actionSubmitting }}
        cancelButtonProps={{ disabled: actionSubmitting }}
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Request ID: <span className="font-medium text-gray-900">{selectedRequest?.id || "-"}</span>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Note</label>
            <textarea
              value={actionNote}
              onChange={(event) => setActionNote(event.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Add an internal note"
            />
          </div>

          {nextStatus === "rejected" && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Rejected Reason</label>
              <textarea
                value={rejectedReason}
                onChange={(event) => setRejectedReason(event.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Enter rejection reason"
              />
            </div>
          )}
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default ChannelPartnerDetails;
